import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ClipboardList, Plus, Send, Trash2, X } from "lucide-react";

import { ApiError, formatCLP } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { EmptyState } from "../../components/EmptyState";
import { Modal } from "../../components/Modal";
import {
  Badge,
  type BadgeTone,
  Button,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
  Textarea,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import { listarBodegas, listarInsumos } from "../inventario/api";
import {
  anularSolicitud,
  aprobarSolicitud,
  crearSolicitud,
  enviarSolicitud,
  evaluarPolitica,
  listarSolicitudes,
  obtenerSolicitud,
  ratificarSolicitud,
  rechazarSolicitud,
  type EstadoSIC,
  type PrioridadSIC,
  type SolicitudCompraCreate,
  type SolicitudCompraRead,
} from "./api";

const ESTADOS: EstadoSIC[] = [
  "Borrador",
  "Solicitada",
  "Aprobada",
  "Rechazada",
  "Comprada",
  "Anulada",
];
const PRIORIDADES: PrioridadSIC[] = ["Normal", "Urgente", "Emergencia"];

const ESTADO_TONE: Record<EstadoSIC, BadgeTone> = {
  Borrador: "slate",
  Solicitada: "brand",
  Aprobada: "emerald",
  Rechazada: "rose",
  Comprada: "brand",
  Anulada: "slate",
};
const PRIORIDAD_TONE: Record<PrioridadSIC, BadgeTone> = {
  Normal: "slate",
  Urgente: "amber",
  Emergencia: "rose",
};

function nroSIC(s: SolicitudCompraRead): string {
  return `${s.anio}-${String(s.numero).padStart(4, "0")}`;
}

export function SolicitudesComprasPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const [creando, setCreando] = useState(false);
  const [detalle, setDetalle] = useState<number | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<EstadoSIC | "">(
    (searchParams.get("estado") as EstadoSIC | null) ?? "",
  );
  // ?ratificacion_pendiente=true llega desde la alerta del dashboard.
  const [soloRatificar, setSoloRatificar] = useState(
    searchParams.get("ratificacion_pendiente") === "true",
  );

  const solicitudesQ = useQuery({
    queryKey: ["compras", "solicitudes", filtroEstado || null, soloRatificar],
    queryFn: () => listarSolicitudes(filtroEstado || undefined, soloRatificar ? true : undefined),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Solicitudes (SIC)"
        icon={ClipboardList}
        subtitle={solicitudesQ.data && (
          <span>({solicitudesQ.data.length})</span>
        )}
        actions={
          <Button icon={Plus} onClick={() => setCreando(true)}>
                    Nueva solicitud
                  </Button>
        }
      />

      <div className="flex items-center gap-2">
        <label className="text-sm text-neutral-600">Estado</label>
        <select
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value as EstadoSIC | "")}
          className="rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
        >
          <option value="">Todas</option>
          {ESTADOS.map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </select>
        <label className="ml-2 flex items-center gap-1.5 text-sm text-neutral-600">
          <input
            type="checkbox"
            checked={soloRatificar}
            onChange={(e) => setSoloRatificar(e.target.checked)}
          />
          Sólo por ratificar
        </label>
      </div>

      {solicitudesQ.isPending && <Cargando />}
      {solicitudesQ.isError && (
        <ErrorCarga error={solicitudesQ.error} onReintentar={() => solicitudesQ.refetch()} />
      )}

      {solicitudesQ.data && solicitudesQ.data.length === 0 && (
        <EmptyState
          icon={ClipboardList}
          title="Sin solicitudes de compra"
          description="Crea una solicitud interna para pedir materiales o servicios."
        />
      )}

      {solicitudesQ.data && solicitudesQ.data.length > 0 && (
        <TableWrap label="Solicitudes de compra">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">N°</th>
                <th scope="col" className="px-3 py-2 text-left">Prioridad</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-left">Justificación</th>
                <th scope="col" className="px-3 py-2 text-right">Monto estimado</th>
                <th scope="col" className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {solicitudesQ.data.map((s) => (
                <tr key={s.id} className="hover:bg-neutral-50">
                  <td className="px-3 py-2 font-mono text-xs text-neutral-700">{nroSIC(s)}</td>
                  <td className="px-3 py-2">
                    <Badge tone={PRIORIDAD_TONE[s.prioridad]}>{s.prioridad}</Badge>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <Badge tone={ESTADO_TONE[s.estado]}>{s.estado}</Badge>
                      {s.ratificacion_pendiente && <Badge tone="amber">Ratificar</Badge>}
                    </div>
                  </td>
                  <td className="px-3 py-2 max-w-xs truncate text-neutral-700">{s.justificacion}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatCLP(s.monto_estimado)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="link" size="sm" onClick={() => setDetalle(s.id)}>
                      Ver / gestionar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {creando && <CrearSolicitudModal onClose={() => setCreando(false)} />}
      {detalle != null && (
        <SolicitudDetailModal id={detalle} onClose={() => setDetalle(null)} />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Crear SIC
// ────────────────────────────────────────────────────────────────────────

interface LineaForm {
  descripcion: string;
  cantidad: string;
  insumoId: string;
  costoUnitario: string;
}

function lineaVacia(): LineaForm {
  return { descripcion: "", cantidad: "1", insumoId: "", costoUnitario: "0" };
}

function CrearSolicitudModal({ onClose }: { onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();

  const [justificacion, setJustificacion] = useState("");
  const [prioridad, setPrioridad] = useState<PrioridadSIC>("Normal");
  const [bodegaId, setBodegaId] = useState("");
  const [lineas, setLineas] = useState<LineaForm[]>([lineaVacia()]);
  const [error, setError] = useState<string | null>(null);

  const bodegasQ = useQuery({ queryKey: ["inventario", "bodegas"], queryFn: listarBodegas });
  const insumosQ = useQuery({ queryKey: ["inventario", "insumos"], queryFn: listarInsumos });

  const monto = useMemo(
    () =>
      lineas.reduce(
        (acc, l) => acc + (Number(l.cantidad) || 0) * (Number(l.costoUnitario) || 0),
        0,
      ),
    [lineas],
  );

  const politicaQ = useQuery({
    queryKey: ["compras", "evaluar", monto, prioridad],
    queryFn: () => evaluarPolitica({ monto, prioridad }),
    enabled: monto > 0,
  });

  const mut = useMutation({
    mutationFn: (p: SolicitudCompraCreate) => crearSolicitud(p),
    onSuccess: (s) => {
      toast.success(`Solicitud ${nroSIC(s)} creada`);
      void qc.invalidateQueries({ queryKey: ["compras", "solicitudes"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  function actualizarLinea(idx: number, patch: Partial<LineaForm>): void {
    setLineas((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx]!, ...patch };
      return next;
    });
  }

  function eliminarLinea(idx: number): void {
    setLineas((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  }

  function submit(): void {
    setError(null);
    if (!justificacion.trim()) return setError("Indica la justificación de la compra");
    const detalles = lineas
      .filter((l) => l.descripcion.trim())
      .map((l) => ({
        descripcion: l.descripcion.trim(),
        cantidad: Number(l.cantidad) || 0,
        insumo_id: l.insumoId ? Number(l.insumoId) : null,
        costo_estimado_unitario: Number(l.costoUnitario) || 0,
      }));
    if (detalles.length === 0) return setError("Agrega al menos una línea con descripción");
    const p: SolicitudCompraCreate = {
      justificacion: justificacion.trim(),
      prioridad,
      detalles,
    };
    if (bodegaId) p.bodega_destino_id = Number(bodegaId);
    mut.mutate(p);
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([justificacion, prioridad, bodegaId, lineas]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nueva solicitud de compra"
      icon={ClipboardList}
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={mut.isPending}>
            {mut.isPending ? "Creando…" : "Crear solicitud"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Textarea
          label="Justificación"
          value={justificacion}
          onChange={(e) => setJustificacion(e.target.value)}
          rows={2}
        />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Select
            label="Prioridad"
            value={prioridad}
            onChange={(e) => setPrioridad(e.target.value as PrioridadSIC)}
          >
            {PRIORIDADES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Select>
          <Select
            label="Bodega destino (opcional)"
            value={bodegaId}
            onChange={(e) => setBodegaId(e.target.value)}
          >
            <option value="">— Sin bodega —</option>
            {(bodegasQ.data ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.nombre}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Líneas
            </div>
            <Button
              variant="link"
              size="sm"
              icon={Plus}
              onClick={() => setLineas((prev) => [...prev, lineaVacia()])}
            >
              Agregar línea
            </Button>
          </div>
          <div className="space-y-2">
            {lineas.map((l, idx) => (
              <div
                key={idx}
                className="grid grid-cols-1 gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-2 md:grid-cols-[1fr_5rem_1fr_7rem_auto] md:items-end"
              >
                <Input
                  label="Descripción"
                  value={l.descripcion}
                  onChange={(e) => actualizarLinea(idx, { descripcion: e.target.value })}
                />
                <Input
                  label="Cantidad"
                  type="number"
                  min={0}
                  value={l.cantidad}
                  onChange={(e) => actualizarLinea(idx, { cantidad: e.target.value })}
                />
                <Select
                  label="Ítem (opcional)"
                  value={l.insumoId}
                  onChange={(e) => {
                    const insumoId = e.target.value;
                    const patch: Partial<LineaForm> = { insumoId };
                    // Al elegir un insumo del catálogo, autocompletar la
                    // descripción y el costo (sin pisar lo ya escrito).
                    const ins = (insumosQ.data ?? []).find(
                      (i) => String(i.id) === insumoId,
                    );
                    if (ins) {
                      if (!l.descripcion.trim()) patch.descripcion = ins.nombre;
                      const costo = ins.ultimo_costo || ins.costo_promedio;
                      if (costo && (l.costoUnitario === "" || l.costoUnitario === "0"))
                        patch.costoUnitario = String(costo);
                    }
                    actualizarLinea(idx, patch);
                  }}
                >
                  <option value="">— Ítem no catalogado —</option>
                  {(insumosQ.data ?? [])
                    .filter((i) => i.activo)
                    .map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.nombre} ({i.unidad_medida})
                      </option>
                    ))}
                </Select>
                <Input
                  label="Costo unit. $"
                  type="number"
                  min={0}
                  value={l.costoUnitario}
                  onChange={(e) => actualizarLinea(idx, { costoUnitario: e.target.value })}
                />
                <Button
                  variant="linkDanger"
                  size="sm"
                  icon={Trash2}
                  onClick={() => eliminarLinea(idx)}
                  disabled={lineas.length <= 1}
                >
                  Quitar
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-neutral-600">Monto estimado</span>
            <span className="font-semibold tabular-nums text-neutral-900">{formatCLP(monto)}</span>
          </div>
          {monto > 0 && (
            <p className="mt-1 text-xs text-neutral-600">
              {politicaQ.isLoading
                ? "Evaluando política…"
                : (politicaQ.data?.resumen ?? "No se pudo evaluar la política.")}
            </p>
          )}
        </div>

        {error && (
          <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        )}
      </div>
      {dialogoDescarte}
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Detalle + ciclo de vida
// ────────────────────────────────────────────────────────────────────────

function SolicitudDetailModal({
  id,
  onClose,
}: {
  id: number;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [rechazando, setRechazando] = useState(false);

  const solQ = useQuery({
    queryKey: ["compras", "solicitud", id],
    queryFn: () => obtenerSolicitud(id),
  });

  function invalidar(): void {
    void qc.invalidateQueries({ queryKey: ["compras", "solicitud", id] });
    void qc.invalidateQueries({ queryKey: ["compras", "solicitudes"] });
  }

  const transicion = useMutation({
    mutationFn: (fn: () => Promise<SolicitudCompraRead>) => fn(),
    onSuccess: () => {
      invalidar();
      toast.success("Solicitud actualizada");
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "No se pudo actualizar"),
  });

  const s = solQ.data;

  return (
    <Modal
      open
      onClose={onClose}
      title={s ? `Solicitud ${nroSIC(s)}` : "Solicitud de compra"}
      subtitle={s ? s.prioridad : undefined}
      icon={ClipboardList}
      size="lg"
    >
      {!s ? (
        <Cargando inline />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={ESTADO_TONE[s.estado]}>{s.estado}</Badge>
            <Badge tone={PRIORIDAD_TONE[s.prioridad]}>{s.prioridad}</Badge>
            {s.ratificacion_pendiente && <Badge tone="amber">Ratificar</Badge>}
            <span className="ml-auto text-sm font-semibold tabular-nums text-neutral-900">
              {formatCLP(s.monto_estimado)}
            </span>
          </div>

          {s.ratificacion_pendiente && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Aprobada automáticamente por falta de aprobador — pendiente de ratificación por la
              directiva (alguien con permiso de aprobar, distinto del solicitante).
            </p>
          )}
          {!s.ratificacion_pendiente && s.fecha_ratificacion && (
            <p className="text-xs text-neutral-500">
              Ratificada el {new Date(s.fecha_ratificacion).toLocaleDateString("es-CL")}.
            </p>
          )}

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Justificación
            </div>
            <p className="text-sm text-neutral-700">{s.justificacion}</p>
          </div>

          {s.motivo_rechazo && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
                Motivo de rechazo
              </div>
              <p className="text-sm text-neutral-700">{s.motivo_rechazo}</p>
            </div>
          )}

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Detalle
            </div>
            {s.detalles.length === 0 ? (
              <p className="text-sm text-neutral-500">Sin líneas.</p>
            ) : (
              <ul className="mt-1 space-y-1 text-sm">
                {s.detalles.map((d) => (
                  <li key={d.id} className="flex justify-between gap-2">
                    <span className="text-neutral-700">
                      {d.descripcion} · {Number(d.cantidad)}
                    </span>
                    <span className="tabular-nums text-neutral-500">
                      {formatCLP(d.costo_estimado_total)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Acciones de ciclo de vida */}
          <div className="flex flex-wrap gap-2 border-t border-neutral-100 pt-3">
            {s.estado === "Borrador" && (
              <>
                <Button
                  size="sm"
                  icon={Send}
                  onClick={() => transicion.mutate(() => enviarSolicitud(s.id))}
                  disabled={transicion.isPending}
                >
                  Enviar
                </Button>
                <Button
                  size="sm"
                  variant="linkDanger"
                  onClick={() => transicion.mutate(() => anularSolicitud(s.id))}
                  disabled={transicion.isPending}
                >
                  Anular
                </Button>
              </>
            )}
            {s.estado === "Solicitada" && (
              <>
                <Button
                  size="sm"
                  icon={Check}
                  onClick={() => transicion.mutate(() => aprobarSolicitud(s.id))}
                  disabled={transicion.isPending}
                >
                  Aprobar
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  icon={X}
                  onClick={() => setRechazando(true)}
                  disabled={transicion.isPending}
                >
                  Rechazar
                </Button>
                <Button
                  size="sm"
                  variant="linkDanger"
                  onClick={() => transicion.mutate(() => anularSolicitud(s.id))}
                  disabled={transicion.isPending}
                >
                  Anular
                </Button>
              </>
            )}
            {s.estado === "Aprobada" && (
              <>
                <p className="text-sm text-neutral-600">
                  Lista para emitir la orden de compra (desde Órdenes de compra).
                </p>
                <Button
                  size="sm"
                  variant="linkDanger"
                  onClick={() => transicion.mutate(() => anularSolicitud(s.id))}
                  disabled={transicion.isPending}
                >
                  Anular
                </Button>
              </>
            )}
            {s.ratificacion_pendiente &&
              (s.estado === "Aprobada" || s.estado === "Comprada") && (
                <Button
                  size="sm"
                  icon={Check}
                  onClick={() => transicion.mutate(() => ratificarSolicitud(s.id))}
                  disabled={transicion.isPending}
                >
                  Ratificar
                </Button>
              )}
          </div>

          {rechazando && (
            <RechazarForm
              solicitudId={s.id}
              onDone={() => {
                setRechazando(false);
                invalidar();
              }}
              onCancel={() => setRechazando(false)}
            />
          )}
        </div>
      )}
    </Modal>
  );
}

function RechazarForm({
  solicitudId,
  onDone,
  onCancel,
}: {
  solicitudId: number;
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const toast = useToast();
  const [motivo, setMotivo] = useState("");

  const mut = useMutation({
    mutationFn: () => rechazarSolicitud(solicitudId, motivo.trim()),
    onSuccess: () => {
      toast.success("Solicitud rechazada");
      onDone();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "No se pudo rechazar"),
  });

  return (
    <div className="space-y-2 rounded-md border border-rose-200 bg-rose-50 p-3">
      <div className="text-sm font-medium text-rose-900">Rechazar solicitud</div>
      <Input label="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} autoFocus />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="secondary" onClick={onCancel} disabled={mut.isPending}>
          Volver
        </Button>
        <Button
          size="sm"
          variant="destructive"
          onClick={() => {
            if (!motivo.trim()) {
              toast.error("Indica el motivo");
              return;
            }
            mut.mutate();
          }}
          disabled={mut.isPending}
        >
          {mut.isPending ? "Rechazando…" : "Rechazar"}
        </Button>
      </div>
    </div>
  );
}
